import { BRAND_IDENTITIES, type BrandIdentity } from "~/config/domains.ts";
import globalsCss from "~/styles/globals.css?url";

/**
 * URL of the built app stylesheet (`styles/globals.css`). It ships as a
 * render-blocking `<link rel="stylesheet">` in every document head (see
 * {@link appStylesheetLink}): the browser does not paint until the sheet has
 * arrived, so the first frame IS the styled frame.
 *
 * WHY it blocks (reverses CLA-646): CLA-646 injected this sheet from an inline
 * script so it would not block first paint, and inlined a hand-curated
 * "critical CSS" subset — the theme tokens plus every layout utility on the
 * then-current marketing landing — so the async swap was layout-identical
 * (CLA-650, CLA-653). That subset was pinned to `routes/clawinstall/index.tsx`,
 * which the public-pages redesign (#1909) deleted; the redesigned pages paint
 * from their own scoped sheet, which the subset never covered, so every full
 * load painted raw HTML and then jumped into place once the sheet arrived
 * (mobile `/` CLS 1.165, Lighthouse performance 100 → 74, the nightly ratchet
 * failing every night). A ~17 KB brotli, same-origin, immutable-cached sheet is
 * not worth a flash-then-reflow on every visit: it blocks, and there is no
 * critical subset to keep in sync with the markup any more.
 *
 * Route-scoped sheets follow the same rule: a design slug's scoped stylesheet
 * is linked from the mounting route's `head()` via a `?url` import (see
 * `lib/clawinstall-marketing-head.ts`), never side-effect-imported from a
 * lazy chunk — a stylesheet the SSR markup depends on must be in the head the
 * server sends, not injected after the route's JavaScript has loaded.
 *
 * @since 1.0.0
 */
export const appStylesheetHref: string = globalsCss;

/**
 * Head link entry for TanStack Router head config
 *
 * @since 1.0.0
 */
export interface HeadLink {
  rel: string;
  href: string;
  crossOrigin?: "anonymous" | "use-credentials";
}

/**
 * Head meta entry for TanStack Router head config
 *
 * @since 1.0.0
 */
interface HeadMeta {
  charSet?: string;
  name?: string;
  property?: string;
  content?: string;
  title?: string;
}

/**
 * Inline head `<script>` entry for TanStack Router head config. TanStack renders
 * `children` as the script body (server: `dangerouslySetInnerHTML`), so the
 * string runs at parse time inside `<head>`.
 *
 * @since 1.0.0
 */
interface HeadScript {
  children: string;
}

/**
 * Head config shape returned by head() functions
 *
 * @since 1.0.0
 */
export interface HeadConfig {
  meta: Array<HeadMeta>;
  /**
   * Head links. Always starts with the render-blocking app stylesheet
   * ({@link appStylesheetLink}) followed by the Google Fonts preconnects; the
   * brand configs append their favicon.
   */
  links: Array<HeadLink>;
  /**
   * Inline head scripts. Holds the stylesheet-loader that injects the css2
   * FONT stylesheets asynchronously (see {@link stylesheetLoaderScript}) so a
   * cross-origin font request never render-blocks; `display=swap` / `block`
   * is baked into each font URL and the metric-matched `* Fallback` faces in
   * `globals.css` keep the pre-webfont paint the same size.
   */
  scripts: Array<HeadScript>;
  /**
   * The css2 font stylesheet hrefs, rendered as blocking `<link rel="stylesheet">`
   * inside a `<noscript>` fallback (see `RootDocument`). This is the no-JS
   * path: without JavaScript the loader script never runs, so the fonts must
   * load the old-fashioned (blocking) way. The app stylesheet is NOT in this
   * list — it is a real `<link>` in {@link HeadConfig.links} and needs no
   * fallback.
   */
  noscriptStylesheetHrefs: Array<string>;
}

/**
 * The render-blocking app stylesheet link, first in every brand's `links`
 * (see {@link appStylesheetHref} for why it blocks).
 *
 * @since 1.0.0
 */
export const appStylesheetLink: HeadLink = { rel: "stylesheet", href: appStylesheetHref };

/**
 * Text fonts for the FlirtMachine / Orca / default theme: DM Sans (`--font-sans`
 * body) + Space Grotesk (`--font-heading`). Both families ride a single css2
 * request. ClawInstall loads DM Sans separately via {@link clawInstallTextFontsHref}
 * (the `theme-clawinstall-customer` body font), alongside Inter and Space Grotesk.
 *
 * @since 1.0.0
 */
const dmSansSpaceGroteskHref =
  "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Space+Grotesk:wght@300..700&display=swap";

/**
 * Text fonts for the ClawInstall theme, consolidated into a SINGLE css2 request:
 * DM Mono (`.pub-time` / `.pub-terminal` / `.phm-number` on the public marketing
 * pages, 400/500 — the 700 lockup mark synthesizes from 500 as on the re-shot
 * board) + DM Sans (`--font-sans` body of the `theme-clawinstall-customer`
 * sub-theme AND the marketing pages, the SAME variable request the board and the
 * FlirtMachine head use, `opsz` axis included) + Inter (`--font-body-*` body of
 * the token-styled legal/login pages) + Space Grotesk (`--font-headline-*` /
 * `--font-label-*` / `--font-heading` headings & labels, shared). All four ride
 * one request. `family=` params must stay alphabetical (DM+Mono < DM+Sans <
 * Inter < Space+Grotesk) per the Google Fonts API. `display=swap` keeps text
 * fonts non-blocking. DM Mono is loaded here — never via the design components'
 * board-only `fonts.css` `@import`.
 *
 * @since 1.0.0
 */
const clawInstallTextFontsHref =
  "https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Inter:wght@400;500;600&family=Space+Grotesk:wght@300..700&display=swap";

/**
 * Material Symbols is subsetted to exactly the glyphs the ClawInstall UI renders
 * via `icon_names=` — this serves a few KB instead of the full ~3,962 KiB
 * variable font. Two rules when touching this:
 *   1. `icon_names` MUST stay alphabetical (Google Fonts API requirement).
 *   2. Re-grep `material-symbols` across packages/web/src whenever an icon is
 *      added — a name missing from this list renders as raw ligature text. The
 *      head.test.ts icon-coverage test enforces this in CI.
 * `display=block` (not `swap`) is Google's recommendation for icon fonts to
 * avoid a flash of raw ligature text. It is loaded async like the text fonts
 * (icons are not the LCP element, so a late icon paint is acceptable), but keeps
 * `display=block` so the glyphs never flash as raw ligature text.
 * https://developers.google.com/fonts/docs/material_symbols#optimize_the_icon_font
 *
 * @since 1.0.0
 */
const materialSymbolsHref =
  "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&icon_names=arrow_forward,celebration,cloud_done,dark_mode,health_and_safety,light_mode,rocket_launch,savings,sentiment_dissatisfied&display=block";

/** Font stylesheets for the FlirtMachine / Orca / default theme (loaded async). */
const flirtMachineFontHrefs: Array<string> = [dmSansSpaceGroteskHref];

/** Font stylesheets for the ClawInstall theme: text fonts + icon subset (loaded async). */
const clawInstallFontHrefs: Array<string> = [clawInstallTextFontsHref, materialSymbolsHref];

/**
 * Injects each href as a non-render-blocking `<link rel="stylesheet">` at
 * runtime. A dynamically-inserted stylesheet is fetched asynchronously and does
 * NOT block the initial paint (unlike a parser-encountered `<link>` in the
 * head), so the cross-origin css2 font stylesheets stay out of the
 * render-blocking set while still applying once loaded (`display=swap`/`block`
 * is baked into each font URL). Only FONT stylesheets go through here — the app
 * stylesheet and every route-scoped sheet are real `<link>`s (see
 * {@link appStylesheetHref}). Idempotent: skips an href whose link already
 * exists, so a re-run (the SSR'd script plus any client re-injection) never
 * duplicates a stylesheet.
 *
 * This function is serialized verbatim into an inline `<head>` script by
 * {@link stylesheetLoaderScript}, so it MUST stay self-contained: reference only
 * the `document` global and its own locals, never an outer-scope binding (a
 * closure variable would be undefined in the inline-script context).
 *
 * @since 1.0.0
 */
export const injectStylesheets = (hrefs: ReadonlyArray<string>): void => {
  for (const href of hrefs) {
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
      continue;
    }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }
};

/**
 * Builds the inline head script that asynchronously loads `hrefs` (the css2
 * font stylesheets). It serializes {@link injectStylesheets} into a
 * self-invoking expression so the exact function covered by tests is what
 * ships in the `<head>`.
 *
 * @since 1.0.0
 */
export const stylesheetLoaderScript = (hrefs: ReadonlyArray<string>): HeadScript => ({
  children: `(${injectStylesheets.toString()})(${JSON.stringify(hrefs)})`,
});

/**
 * Global links shared by all domains: the render-blocking app stylesheet first
 * (so it is the first request the preload scanner issues), then the Google
 * Fonts preconnects. The `gstatic.com` preconnect (with `crossorigin`) is
 * where the woff2 font bytes are fetched from, so warming it early cuts the
 * async font load. No FONT stylesheet is shipped here (or in any per-brand
 * `links`): those are injected asynchronously via the stylesheet loader script
 * (see `scripts` / {@link stylesheetLoaderScript}) so a cross-origin request
 * never render-blocks first paint.
 *
 * @since 1.0.0
 */
export const globalLinks: Array<HeadLink> = [
  appStylesheetLink,
  {
    rel: "preconnect",
    href: "https://fonts.googleapis.com",
  },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
];

/**
 * Global meta tags shared by all domains: charset and viewport
 *
 * @since 1.0.0
 */
export const globalMeta: Array<HeadMeta> = [
  { charSet: "utf-8" },
  { name: "viewport", content: "width=device-width, initial-scale=1" },
];

/**
 * Default head config for the main FlirtMachine site
 *
 * @since 1.0.0
 */
export const defaultHeadConfig = (): HeadConfig => ({
  meta: [
    ...globalMeta,
    { title: "FlirtMachine" },
  ],
  links: [...globalLinks],
  scripts: [stylesheetLoaderScript(flirtMachineFontHrefs)],
  noscriptStylesheetHrefs: flirtMachineFontHrefs,
});

/**
 * Head config for ClawInstall domain (clawinstall.ai)
 *
 * @since 1.0.0
 */
export const clawInstallHeadConfig = (identity: BrandIdentity = BRAND_IDENTITIES.clawinstall): HeadConfig => ({
  meta: [
    ...globalMeta,
    { title: `${identity.displayName} — Professional AI Agent Setup` },
    { name: "description", content: "Professional OpenClaw installation in under an hour." },
    { name: "theme-color", content: "#ffffff" },
    { property: "og:site_name", content: identity.displayName },
    { property: "og:type", content: "website" },
  ],
  links: [
    ...globalLinks,
    { rel: "icon", href: identity.faviconHref },
  ],
  // DM Sans (customer sub-theme body) + Inter (token-page body) + Space
  // Grotesk (headings/labels) in ONE css2 request and the separately-subsetted
  // Material Symbols icon font — injected async so no cross-origin font
  // request render-blocks.
  scripts: [stylesheetLoaderScript(clawInstallFontHrefs)],
  noscriptStylesheetHrefs: clawInstallFontHrefs,
});

/**
 * Head config for FlirtMachine domain (flirtmachine.app / fm.test)
 *
 * @since 1.0.0
 */
export const flirtMachineHeadConfig = (): HeadConfig => ({
  meta: [
    ...globalMeta,
    { title: "FlirtMachine" },
    { name: "description", content: "FlirtMachine — AI-powered conversational personas." },
    { name: "theme-color", content: "#09090b" },
    { property: "og:site_name", content: "FlirtMachine" },
    { property: "og:type", content: "website" },
  ],
  links: [...globalLinks],
  scripts: [stylesheetLoaderScript(flirtMachineFontHrefs)],
  noscriptStylesheetHrefs: flirtMachineFontHrefs,
});

/**
 * Head config for the Orcaloops domain (orcaloops.ai / orca.theinfinityguides.com / orca.test)
 *
 * @since 1.0.0
 */
export const orcaHeadConfig = (): HeadConfig => ({
  meta: [
    ...globalMeta,
    { title: "Orcaloops" },
    {
      name: "description",
      content: "Orchestrate coding agents toward outcomes on hosted runners — outcomes in, evidence out.",
    },
    { name: "theme-color", content: "#09090b" },
    { property: "og:site_name", content: "Orcaloops" },
    { property: "og:type", content: "website" },
  ],
  links: [...globalLinks],
  scripts: [stylesheetLoaderScript(flirtMachineFontHrefs)],
  noscriptStylesheetHrefs: flirtMachineFontHrefs,
});

/**
 * Returns the appropriate head config based on hostname flags
 *
 * @since 1.0.0
 */
export const getHeadConfigForHostname = (
  hostname?: { isClawInstall: boolean; isOrca: boolean; isFlirtMachine: boolean },
  identity?: BrandIdentity,
): HeadConfig => {
  if (hostname?.isClawInstall) {
    return clawInstallHeadConfig(identity);
  }
  if (hostname?.isOrca) {
    return orcaHeadConfig();
  }
  if (hostname?.isFlirtMachine) {
    return flirtMachineHeadConfig();
  }
  return defaultHeadConfig();
};
